import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { AppDispatch } from '../../redux/store';
import { setUserFromCustomAuth } from '../../redux/authSlice';
import { initiateEmailAuth, respondToEmailChallenge, resendOtpCode, AuthChallenge } from '../../services/cognitoAuth';
import { GET_USER_BY_USERNAME } from '../../graphql/queries/users';
import TextInput from '../common/TextInput';
import Button from '../common/Button';
import { useCustomerPublic } from '../../hooks/useCustomerPublic';

interface CustomLoginProps {}

const CustomLogin: React.FC<CustomLoginProps> = () => {
  const { t } = useTranslation('login');
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { customerData } = useCustomerPublic();
  
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [challenge, setChallenge] = useState<AuthChallenge | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [getUserByUsername] = useLazyQuery(GET_USER_BY_USERNAME);

  const extractUsernameFromToken = (idToken: string): string | null => {
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      return payload['cognito:username'] || payload.username || null;
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  };

  const handleAuthSuccess = async (authResult: any, userEmail: string) => {
    try {
      let username = null;
      
      if (authResult.idToken) {
        username = extractUsernameFromToken(authResult.idToken);
      }
      
      if (username) {
        const { data } = await getUserByUsername({ variables: { username } });
        const userRole = data?.getUserByUsername?.role || '';
        
        dispatch(setUserFromCustomAuth({
          email: userEmail,
          role: userRole,
          access_token: authResult.accessToken,
          id_token: authResult.idToken,
          refresh_token: authResult.refreshToken,
        }));
      } else {
        dispatch(setUserFromCustomAuth({
          email: userEmail,
          access_token: authResult.accessToken,
          id_token: authResult.idToken,
          refresh_token: authResult.refreshToken,
        }));
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Error fetching user role:', error);
      dispatch(setUserFromCustomAuth({
        email: userEmail,
        access_token: authResult.accessToken,
        id_token: authResult.idToken,
        refresh_token: authResult.refreshToken,
      }));
      navigate('/dashboard');
    }
  };

  const handleEmailSubmit = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await initiateEmailAuth(email);
      
      if (result.challenge) {
        setChallenge(result.challenge);
      } else if (result.accessToken) {
        await handleAuthSuccess(result, email);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async () => {
    if (!otpCode) {
      setError('Please enter the verification code');
      return;
    }

    if (!challenge) {
      setError('Session expired. Please try again.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await respondToEmailChallenge(email, otpCode, challenge.session);
      
      if (result.accessToken) {
        await handleAuthSuccess(result, email);
      } else if (result.challenge) {
        setChallenge(result.challenge);
      }
    } catch (error: any) {
      setError(error.message || 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    // Clear the OTP code input
    setOtpCode('');

    setIsLoading(true);
    setError('');

    try {
      // Use resendOtpCode to send a new OTP while maintaining session consistency
      const result = await resendOtpCode(email);

      if (result.challenge) {
        // Update the challenge with the new session
        setChallenge(result.challenge);
      } else if (result.accessToken) {
        // If we got tokens directly, update auth state
        await handleAuthSuccess(result, email);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  if (challenge) {
    return (
      <div className="custom-login">
        <h2 className="leading-[38px]">メールアドレス認証</h2>
        <p className="mb-4">{email} に送信された認証コードを入力してください。</p>

        <TextInput
          label="認証コード"
          inputValue={otpCode}
          onChange={setOtpCode}
          placeholder="6桁の数字を入力してください"
          errorMessage={error}
        />

        <div className="flex gap-2 mt-4">
          <Button
              label="再送信"
              onClick={handleResendCode}
              addedClass="bg-gray-500 hover:bg-gray-600"
          />
          <Button
            label={isLoading ? '認証中...' : '認証'}
            onClick={handleOtpSubmit}
            addedClass={isLoading ? 'opacity-50 cursor-not-allowed' : ''}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="custom-login">
      <h2 className="leading-[38px]">{customerData?.companyName || ''}</h2>
      <h3 className="leading-[30px]">{t('login')}</h3>
      <br/>

      <TextInput
        label={t('email address')}
        inputValue={email}
        onChange={setEmail}
        placeholder="登録されているメールアドレスを入力してください"
        errorMessage={error}
      />

      <Button 
        label={isLoading ? '送信中...' : t('send verification code')}
        onClick={handleEmailSubmit}
        addedClass={isLoading ? 'opacity-50 cursor-not-allowed' : ''}
      />
    </div>
  );
};

export default CustomLogin;
